<template>
  <b-table
    :data="items"
    :mobile-cards="false"
    narrowed
    sticky-header
    :row-class="rowClass"
    :height="height"
  >
    <b-table-column label="" width="40" centered v-slot="props">
      <b-tooltip v-show="props.row.error" position="is-right">
        <b-icon icon="information" type="is-danger"></b-icon>
        <template v-slot:content>
          {{ props.row.error }}
        </template>
      </b-tooltip>
    </b-table-column>

    <b-table-column
      field="code"
      :label="$t('attributes.group_code')"
      width="100"
      centered
      sortable
      v-slot="props"
    >
      <b-tooltip type="is-light" position="is-right">
        <p>{{ props.row.code }}</p>
        <template v-slot:content>
          <p v-html="props.row.name"></p>
        </template>
      </b-tooltip>
    </b-table-column>
    <b-table-column
      field="departure_date"
      :label="$t('attributes.departure_date')"
      width="100"
      sortable
      centered
      v-slot="props"
    >
      {{ formatDate(props.row.departure_date) }}
    </b-table-column>

    <b-table-column
      :label="$t('attributes.shelve_status')"
      width="80"
      centered
      v-slot="props"
    >
      <b-switch
        :ref="`shelve-input-${props.row.code}`"
        v-model="props.row.shelve_on"
        :disabled="props.row.is_deprecated"
        @input="modified(props.row)"
      ></b-switch>
    </b-table-column>

    <b-table-column
      :label="$t('attributes.situation')"
      width="130"
      centered
      v-slot="props"
    >
      <b-select
        :ref="`situation-input-${props.row.code}`"
        v-model="props.row.situation"
        @input="modified(props.row)"
      >
        <option
          v-for="option in situationTypes"
          :key="option.id"
          :value="option.id"
        >
          <!-- eslint-disable-next-line -->
          <p v-html="option.name"></p>
        </option>
      </b-select>
    </b-table-column>

    <b-table-column
      :label="$t('attributes.airline')"
      width="320"
      centered
      v-slot="props"
    >
      <b-autocomplete
        :ref="`airline-input-${props.row.code}`"
        v-model="props.row.airlineValue"
        :data="props.row.airlines"
        field="display_name"
        :placeholder="$t('message.please_input') + $t('attributes.airline')"
        :open-on-focus="true"
        @typing="search => onSelectingAirline(props.row, search)"
        @select="option => onSelectedAirline(props.row, option)"
      >
        <template #default="air">
          <div class="media">
            <div class="media-left" style="width: 20px">
              {{ air.option.code }}
            </div>
            <div class="media-content">
              {{ air.option.name }}
              ({{ air.option.english_name }})
            </div>
          </div>
        </template>
        <template #empty>
          No results found
        </template>
      </b-autocomplete>
    </b-table-column>

    <b-table-column :label="$t('attributes.operator_remark')" v-slot="props">
      <b-input
        :ref="`remark-input-${props.row.code}`"
        v-model="props.row.remark5"
        @keypress.enter.exact.native="
          finishText(props.row, props.index + 1, 'remark')
        "
        @keypress.enter.shift.exact.native="
          finishText(props.row, props.index - 1, 'remark')
        "
        @input="modified(props.row)"
      />
    </b-table-column>

    <b-table-column
      width="50"
      :label="$t('attributes.function')"
      centered
      v-slot="props"
    >
      <div class="buttons is-centered">
        <b-tooltip :label="$t('attributes.restore')">
          <b-button
            v-if="!props.row.editing"
            type="is-info"
            icon-left="restore"
            outlined
            @click.prevent="restore(props.row)"
          >
          </b-button>
        </b-tooltip>
      </div>
    </b-table-column>

    <template #empty>
      <section class="section" style="height: 100%">
        <div v-if="!loading" class="content has-text-grey has-text-centered">
          <p>
            <b-icon icon="emoticon-sad" size="is-large" />
          </p>
        </div>
      </section>
    </template>
  </b-table>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'BatchBasicTable',

  props: {
    groups: {
      Type: Array,
      required: true
    },
    airlines: {
      required: true,
      type: Array
    },
    situationTypes: {
      required: true,
      type: Array
    },
    height: Number,
    loading: Boolean
  },
  data() {
    return {
      items: this.extractGroups(this.groups)
    }
  },
  watch: {
    groups(list) {
      this.items = this.extractGroups(list)
    }
  },
  methods: {
    extractGroups(groups) {
      return groups.map(group => {
        const air = this.airlines.find(a => a.code === group.airline)
        return {
          code: group.code,
          departure_date: group.departure_date,
          name: group.name,
          shelve_on: group.shelve_on,
          is_deprecated: group.is_deprecated,
          remark5: group.remark5,
          situation: group.situation,
          airlines: this.airlines,
          airline: group.airline,
          airlineValue: air && air.display_name,
          dirty: false,
          error: null,
          origin: group
        }
      })
    },
    onSelectingAirline: debounce(function(group, search) {
      const filter = search.toUpperCase().trim()
      group.airlines = this.airlines.filter(airline =>
        airline.display_name.toUpperCase().includes(filter)
      )
    }, 500),
    onSelectedAirline(group, option) {
      group.airline = option && option.code.trim()
      this.modified(group)
    },
    finishText(group, next, field) {
      this.modified(group)

      let index = (next + this.items.length) % this.items.length
      if (index >= 0 && index < this.items.length) {
        const nextGroup = this.items[index]

        setTimeout(() => {
          const name = `${field}-input-${nextGroup.code}`
          const textarea = this.$refs[name].getElement()
          if (textarea) {
            textarea.selectionStart = 0
            textarea.selectionEnd = 0
            textarea.focus()
          }
        }, 0)
      }
    },
    modified(group) {
      group.dirty =
        group.shelve_on !== group.origin.shelve_on ||
        group.remark5 !== group.origin.remark5 ||
        group.situation !== group.origin.situation ||
        group.airline !== group.origin.airline

      this.validate(group)
    },
    restore(group) {
      group.shelve_on = group.origin.shelve_on
      group.remark5 = group.origin.remark5
      group.situation = group.origin.situation

      const air = this.airlines.find(a => a.code === group.origin.airline)
      this.$refs[`airline-input-${group.code}`].setSelected(air)

      group.dirty = false
      group.error = false
    },
    isDirty() {
      return this.items.filter(x => x.dirty).length > 0
    },
    isEditing() {
      return this.items.filter(x => x.editing).length > 0
    },
    isValidate() {
      for (const group of this.items) {
        if (!this.validate(group)) {
          return false
        }
      }

      return true
    },
    validate(group) {
      if (!group.airline) {
        group.error = '航空公司必填'
        return false
      }

      group.error = null
      return true
    },
    getData() {
      return this.items
        .filter(group => group.dirty)
        .map(group => ({
          code: group.code,
          shelve_on: group.shelve_on,
          remark5: group.remark5,
          situation: group.situation,
          airline: group.airline
        }))
    },
    rowClass(group) {
      if (group.error) {
        return 'has-background-danger-light'
      } else if (group.dirty) {
        return 'has-background-warning'
      } else {
        return null
      }
    }
  }
}
</script>

<style scoped></style>
